import React, { useState } from 'react'
import {
  Grid,
  Heading,
  Button,
  Layout,
  Form,
  Input,
} from '@enterprise-ui/canvas-ui-react'
import { useAuth } from '@praxis/component-auth'
import { useFormik } from 'formik'
import { useMutation, useLazyQuery } from '@apollo/client'
import * as Yup from 'yup'
import { useGlobalForm } from '../Context/GlobalFormStateContext'
import { GlobalFormProvider } from '../Context/GlobalFormStateContext'
import { UPDATE_CASE, GET_CASE, Case, CaseResponse } from '../Common/Queries'
import { useToaster } from '@enterprise-ui/canvas-ui-react'
import { useNavigate } from 'react-router-dom'
import { SET_FIELD } from '../Common/Constants'
import { CaseStatus } from '../Services/ConstantData'
import ReopenModal from '../Common/ReopenModal'
import '../Custom.css'

interface UpdateCaseVar {
  id: number
  input: any
}

const ReopenCaseContent: React.FC = () => {
  useAuth()
  const makeToast = useToaster()
  const navigate = useNavigate()
  const [, dispatch] = useGlobalForm()
  const [loading, setLoading] = useState(false)
  const [caseData, setCaseData] = useState<CaseResponse | undefined>(undefined)

  const caseIdFormik = useFormik({
    initialValues: { caseId: '' },
    validationSchema: Yup.object().shape({
      caseId: Yup.string().required('Please enter a Case ID'),
    }),
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: () => {},
  })

  const modalFormik = useFormik({
    initialValues: {
      status: '',
      isReopen: false,
      reopenDetails: {
        reopenDate: '',
        reopenUser: '',
        reopenReason: '',
        reopenReasonId: '',
        justificationForReopen: '',
        previousResolutionDate: null,
        previousResolutionUser: '',
        previousResolutionCode: '',
        previousResolutionCodeText: '',
        previousResolutionText: '',
        previousResolutionAmount: null,
        previousResolutionDocumentNumber: '',
      },
      resolutionDate: null,
      resolutionCode: undefined,
      resolutionCodeText: null,
      resolutionText: undefined,
      resolutionAmount: null,
      auditEntries: [],
      workflow: undefined,
      lanId: null,
    },
    onSubmit: async (values) => {
      const parsedId = parseInt(caseIdFormik.values.caseId, 10)
      if (isNaN(parsedId)) return
      setLoading(true)
      try {
        const {
          status,
          isReopen,
          reopenDetails: rawReopenDetails,
          resolutionDate,
          resolutionCode,
          resolutionCodeText,
          resolutionText,
          resolutionAmount,
          auditEntries,
          workflow,
          lanId,
        } = values
        const { justificationForReopen: _jfr, ...reopenDetails } =
          rawReopenDetails || {}
        await updateCase({
          variables: {
            id: parsedId,
            input: {
              status,
              isReopen,
              reopenDetails,
              resolutionDate,
              resolutionCode,
              resolutionCodeText,
              resolutionText,
              resolutionAmount,
              auditEntries,
              workflow,
              lanId,
            },
          },
        })
      } catch (error) {
        console.error('Error reopening case:', error)
        makeToast({
          type: 'error',
          heading: 'Error',
          message: 'Failed to reopen case',
        })
        setLoading(false)
      }
    },
  })

  const [updateCase] = useMutation<any, UpdateCaseVar>(UPDATE_CASE, {
    onCompleted: () => {
      makeToast({
        type: 'success',
        heading: 'Reopen',
        message: 'Case reopened successfully',
      })
      const caseId = parseInt(caseIdFormik.values.caseId, 10)
      setLoading(false)
      caseIdFormik.resetForm()
      setCaseData(undefined)
      if (!isNaN(caseId)) {
        navigate(`/cases/${caseId}`)
      }
    },
    onError: (error) => {
      makeToast({
        type: 'error',
        heading: 'Error',
        message: error.message || 'Failed to reopen case',
      })
      setLoading(false)
    },
  })

  const [fetchCase, { loading: fetching }] = useLazyQuery<Case>(GET_CASE, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const fetched = data?.getCase
      if (!fetched) {
        makeToast({
          type: 'error',
          heading: 'Not Found',
          message: 'No case found with that ID',
        })
        return
      }
      const allowedStatuses: string[] = [CaseStatus.CLOSED, CaseStatus.RESOLVED]
      if (!fetched.status || !allowedStatuses.includes(fetched.status)) {
        makeToast({
          type: 'error',
          heading: 'Not Allowed',
          message: 'Only cases with status Closed or Resolved can be reopened',
        })
        return
      }
      const eighteenMonthsAgo = new Date()
      eighteenMonthsAgo.setMonth(eighteenMonthsAgo.getMonth() - 18)
      const caseCreateDate = fetched.createTimestamp
        ? new Date(fetched.createTimestamp)
        : null
      if (!caseCreateDate || caseCreateDate < eighteenMonthsAgo) {
        makeToast({
          type: 'error',
          heading: 'Not Allowed',
          message:
            'Only cases created within the last 18 months can be reopened',
        })
        return
      }
      setCaseData(fetched)
      dispatch({
        type: SET_FIELD,
        payload: [
          { id: 'existingCaseData', value: fetched },
          { id: 'reOpenModalFlag', value: true },
        ],
      })
      modalFormik.setFieldValue('workflow', fetched.workflow)
      modalFormik.setFieldValue('lanId', fetched.userProfile?.userId)
    },
    onError: (error) => {
      makeToast({
        type: 'error',
        heading: 'Error',
        message: error.message || 'Failed to fetch case',
      })
    },
  })

  const handleOpenModal = async () => {
    const errors = await caseIdFormik.validateForm()
    if (Object.keys(errors).length > 0) {
      caseIdFormik.setTouched({ caseId: true })
      return
    }
    const parsedId = parseInt(caseIdFormik.values.caseId, 10)
    fetchCase({ variables: { id: parsedId } })
  }

  const isValidCaseId =
    !!caseIdFormik.values.caseId.trim() &&
    !isNaN(parseInt(caseIdFormik.values.caseId, 10))

  return (
    <Layout.Body includeRail>
      <Grid.Container justify="space-between" className="hc-pa-none">
        <Grid.Item xs={12} md={7}>
          <Grid.Container justify="space-between">
            <Grid.Item>
              <Heading
                size={6}
                className="hc-mt-normal hc-mb-normal hc-ml-none"
              >
                RE-OPEN CASE
              </Heading>
            </Grid.Item>
          </Grid.Container>
          <Form>
            <Grid.Container className="hc-pa-md">
              {/* Case ID Field */}
              <Grid.Item xs={12}>
                <Input.Label htmlFor="caseId" required>
                  Case ID
                </Input.Label>
                <Input.Text
                  id="caseId"
                  placeholder="Enter case ID"
                  value={caseIdFormik.values.caseId}
                  onChange={(e: any) =>
                    caseIdFormik.setFieldValue('caseId', e.target.value)
                  }
                  errorText={
                    caseIdFormik.touched.caseId
                      ? caseIdFormik.errors.caseId
                      : ''
                  }
                  disabled={fetching || loading}
                />
              </Grid.Item>

              {/* Submit Button */}
              <Grid.Item xs={12} className="hc-mt-normal">
                <Grid.Container justify="flex-end" spacing="dense">
                  <Grid.Item>
                    <Button
                      type="primary"
                      onClick={handleOpenModal}
                      disabled={!isValidCaseId || fetching || loading}
                    >
                      {fetching ? 'Loading...' : 'Reopen Case'}
                    </Button>
                  </Grid.Item>
                </Grid.Container>
              </Grid.Item>
            </Grid.Container>
          </Form>
        </Grid.Item>
      </Grid.Container>
      <ReopenModal
        headingText="Reopen the case"
        formik={modalFormik}
        reopenDetails={caseData}
        saveStatus={modalFormik.handleSubmit}
      />
    </Layout.Body>
  )
}

const ReopenCase: React.FC = () => {
  return (
    <GlobalFormProvider>
      <ReopenCaseContent />
    </GlobalFormProvider>
  )
}

export default ReopenCase
